import { useIntl } from 'react-intl';
import { subjectOfTreatmentNames } from 'Routes/DoctorsOrders/Document/config';
import { useAppSelector } from 'Store/index';
import { uniq } from 'Utility/ramdaReplacement';
import colors from '../../../../config/theme/colors';
import { Container, Item } from 'Components/Grid';
import FilePreviewBox from 'Components/_NewElements/FilePreviewBox';
import EditDialog from '../Form/Sessions/components/Attachments/EditDialog';
import React from 'react';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import AddNewDialog from '../Form/Sessions/components/Attachments/AddNewDialog';
import DeleteDialog from 'Components/HistoryRowControls/DeleteDialog';

export const rtmsSubjectName = (name: string, fm: (id: string) => string) =>
  subjectOfTreatmentNames.includes(name)
    ? fm(`rtms.subjectOfTreatmentNames.${name}`)
    : name === 'supportPicture'
      ? fm(`rtms.subjectOfTreatment.supportPictures`)
      : name;

const TargetName = ({ name, fm }: ITargetName) => {
  const tName = rtmsSubjectName(name, fm);

  return <div style={{ color: colors.primary }}>{tName}</div>;
};

interface ITargetName {
  name: string;
  fm: (s: string) => string;
}

const TreatmentTargetPictures = () => {
  const stateFiles = useAppSelector(s => s.documents.files);
  const rtmsPictures = structuredClone(stateFiles ?? []).filter(f => f.subType === 'rtms');

  const treatmentTargets = uniq(rtmsPictures.map(pic => pic.referenceType)).filter(p => p) as string[];

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const [deletingFile, setDeletingFile] = React.useState<IDocumentWithTypeV2<'filestorefile'> | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [newData, setNewData] = React.useState<Partial<
    Pick<TFILE_METADATA, 'date' | 'name' | 'subType' | 'referenceType'>
  > | null>(null);

  // Temporary attachments used for handling the attachment form
  const [tempAttachments, setTempAttachments] = React.useState<File[] | null>(null);
  const handleTempFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTempAttachments(prev => [...(prev ?? []), file]);
  };
  const editFileData = (data: Record<string, string>) => {
    const editedData = data;
    newData && setNewData({ ...newData, ...editedData });
  };

  const onCloseEditing = () => {
    setEditingId(null);
    setNewData(null);
  };

  const editingFileData = rtmsPictures.find(f => f._id === editingId) ?? null;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ActionButtonRounded
          text="rtms.subjectOfTreatment.addPicture"
          onClick={() => setTempAttachments([])}
          height={3.5}
          width={'auto'}
          fontSize={14}
        />
      </div>
      {treatmentTargets.map(target => {
        const thisTargetPictures = rtmsPictures
          .filter(rt => rt.referenceType === target)
          .sort((a, b) => {
            const aDate = a.date ? new Date(a.date) : new Date(a._docCreateDate);
            const bDate = b.date ? new Date(b.date) : new Date(b._docCreateDate);
            return bDate.valueOf() - aDate.valueOf();
          });

        const thisTargetPicture = thisTargetPictures[0];
        if (!thisTargetPicture) return null;

        return (
          <div key={target} style={{ paddingBottom: '2rem' }}>
            <h3>
              <TargetName name={target} fm={fm} />
            </h3>
            <Container>
              <Item xs={4}>
                <a href={`/attachments/${thisTargetPicture.fileContentId}`} rel="noreferrer" target="_blank">
                  <FilePreviewBox
                    name={thisTargetPicture.name}
                    date={thisTargetPicture.date ?? undefined}
                    thumbnail={undefined}
                    file={thisTargetPicture}
                    controls={{
                      edit: {
                        onClick: () => {
                          setEditingId(thisTargetPicture._id);
                          setNewData({});
                        },
                      },
                    }}
                  />
                </a>
              </Item>
              <Item xs={1} />
              <Item xs={true}>
                <Container rowSpacing={2} columnSpacing={2}>
                  {thisTargetPictures.slice(1).map((file, i) => (
                    <Item xs={6} key={file._id}>
                      <a
                        style={{ width: '100%' }}
                        key={i}
                        href={`/attachments/${file.fileContentId}`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <FilePreviewBox
                          date={file.date ?? undefined}
                          name={file.name}
                          file={file}
                          controls={{
                            edit: {
                              onClick: () => {
                                setEditingId(file._id);
                                setNewData({});
                              },
                            },
                          }}
                        />
                      </a>
                    </Item>
                  ))}
                </Container>
              </Item>
            </Container>
          </div>
        );
      })}
      {deletingFile && (
        <DeleteDialog document={deletingFile} open={!!deletingFile} setOpen={() => setDeletingFile(null)} />
      )}
      <EditDialog
        editingId={editingId}
        editingFileData={editingFileData}
        onCloseEditing={onCloseEditing}
        newData={newData}
        editFileData={editFileData}
      />
      <AddNewDialog
        newAttachments={tempAttachments}
        setNewAttachments={setTempAttachments}
        handleFileChange={handleTempFileChange}
      />
    </>
  );
};

export default TreatmentTargetPictures;
